/**
 * Assessment API utilities for saving assessments and sending emails
 */

import { getAuthToken } from './auth';
import { ensureSubmitterDetails, SUBMISSION_CANCELLED_MESSAGE } from './sharedAccount';

const cancelledResult = () => ({
  success: false,
  cancelled: true,
  error: 'Submission cancelled',
  message: SUBMISSION_CANCELLED_MESSAGE
});

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/**
 * Save assessment data and PDF to MongoDB
 * @param {Object} userDetails - User information
 * @param {Object} assessmentResponses - Assessment form responses
 * @param {Blob} pdfBlob - PDF blob from jsPDF or server
 * @param {string} reportText - Generated report text
 * @param {string} assessmentType - Type of assessment (Building, Bridge, Tower)
 * @returns {Promise<Object>} Response with assessmentId
 */
export async function saveAssessmentToDatabase(
  userDetails,
  assessmentResponses,
  pdfBlob,
  reportText,
  assessmentType = 'Building'
) {
  try {
    console.log('🔵 saveAssessmentToDatabase called with:', { userDetails, assessmentType });
    console.log('🔵 Assessment Responses received:');
    console.log('  - Type:', typeof assessmentResponses);
    console.log('  - Keys:', assessmentResponses ? Object.keys(assessmentResponses) : 'null');
    console.log('  - Full data:', JSON.stringify(assessmentResponses, null, 2));
    
    // Convert PDF blob to base64
    const pdfBase64 = await blobToBase64(pdfBlob);
    console.log('🔵 PDF converted to base64, length:', pdfBase64.length);

    const payload = {
      userDetails: {
        name: userDetails.name || '',
        email: userDetails.email || '',
        phoneCountryCode: userDetails.phoneCountryCode || '+91',
        phone: userDetails.phone || '',
        organization: userDetails.organization || '',
        structureType: assessmentType,
        q1: userDetails.q1 || '',
        q1Other: userDetails.q1Other || '',
        yearOfConstruction: userDetails.yearOfConstruction || null,
        location: userDetails.location || ''
      },
      assessmentResponses: assessmentResponses || {},
      pdfBuffer: pdfBase64,
      reportText: reportText || '',
      assessmentType: assessmentType
    };

    console.log('🔵 Payload to send:');
    console.log('  - userDetails:', payload.userDetails);
    console.log('  - assessmentResponses keys:', Object.keys(payload.assessmentResponses));
    console.log('  - assessmentResponses sample:', JSON.stringify(payload.assessmentResponses).substring(0, 500));
    console.log('  - pdfBuffer length:', payload.pdfBuffer.length);
    console.log('  - reportText length:', payload.reportText.length);
    console.log('  - assessmentType:', payload.assessmentType);

    console.log('🔵 Making POST request to:', `${API_BASE_URL}/api/save-assessment`);
    const response = await fetch(`${API_BASE_URL}/api/save-assessment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify(payload)
    });

    console.log('🔵 Response status:', response.status);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('❌ Save failed:', errorData);
      console.error('❌ Full error details:', JSON.stringify(errorData, null, 2));
      throw new Error(errorData.details || errorData.error || 'Failed to save assessment');
    }

    const data = await response.json();
    console.log('✅ Assessment saved:', data.assessmentId);
    return data;
  } catch (error) {
    console.error('❌ Error saving assessment:', error);
    throw error;
  }
}

/**
 * Send assessment completion email to user
 * @param {Object} userDetails - User information with email
 * @param {string} assessmentType - Type of assessment
 * @param {Blob} pdfBlob - Optional PDF blob to attach
 * @param {string} assessmentId - Optional assessment ID from database
 * @returns {Promise<Object>} Response with success status
 */
export async function sendAssessmentEmail(
  userDetails,
  assessmentType = 'Building',
  pdfBlob = null,
  assessmentId = null
) {
  try {
    console.log('🔵 sendAssessmentEmail called for:', userDetails.email);
    
    let pdfBase64 = null;
    if (pdfBlob) {
      pdfBase64 = await blobToBase64(pdfBlob);
      console.log('🔵 PDF attached to email, length:', pdfBase64.length);
    }

    const payload = {
      userDetails: {
        name: userDetails.name || '',
        email: userDetails.email || ''
      },
      assessmentType: assessmentType,
      pdfBuffer: pdfBase64,
      assessmentId: assessmentId
    };

    console.log('🔵 Making POST request to:', `${API_BASE_URL}/api/send-assessment-email`);
    const response = await fetch(`${API_BASE_URL}/api/send-assessment-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    console.log('🔵 Email response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json();
      console.error('❌ Email failed:', errorData);
      throw new Error(errorData.error || 'Failed to send email');
    }

    const data = await response.json();
    console.log('✅ Email sent successfully');
    return data;
  } catch (error) {
    console.error('❌ Error sending email:', error);
    throw error;
  }
}

/**
 * Save assessment and send email in one operation
 * @param {Object} userDetails - User information
 * @param {Object} assessmentResponses - Assessment form responses
 * @param {Blob} pdfBlob - PDF blob
 * @param {string} reportText - Generated report text (saved to DB but not sent in email)
 * @param {string} assessmentType - Type of assessment
 * @returns {Promise<Object>} Combined response
 */
export async function saveAndEmailAssessment(
  userDetails,
  assessmentResponses,
  pdfBlob,
  reportText,
  assessmentType = 'Building'
) {
  try {
    // Validate required fields
    if (!userDetails.email || !userDetails.name) {
      throw new Error('User email and name are required');
    }
    // New flow: call backend /api/submit-assessment which will
    // - generate comprehensive AI report on SERVER (60 KB+)
    // - create the server-formatted PDF from server-generated report
    // - upload PDF to Cloudinary
    // - save assessment to DB with PDF & cloudinary metadata
    // - send client and admin emails
    console.log('🔵 Submitting assessment to server for full processing...');
    console.log('🔵 Server will generate comprehensive report and create PDF');
    console.log('⚠️ NOT sending frontend report - server will generate its own');
    const submitter = await ensureSubmitterDetails();
    if (submitter.cancelled) return cancelledResult();
    const payload = {
      userDetails: userDetails,
      assessmentResponses: assessmentResponses,
      // reportText: reportText || '',  // DO NOT send - let server generate
      assessmentType: assessmentType,
      submitterDetails: submitter.details
    };

    const response = await fetch(`${API_BASE_URL}/api/submit-assessment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAuthToken()}` },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('❌ submit-assessment failed:', err);
      throw new Error(err.error || err.details || 'Failed to submit assessment');
    }

    const data = await response.json();
    console.log('✅ submit-assessment result:', data);
    return { success: true, assessmentId: data.assessmentId, cloudinaryUrl: data.cloudinaryUrl };
  } catch (error) {
    console.error('❌ Error in save and email operation:', error);
    
    // Return partial success information
    return {
      success: false,
      error: error.message,
      message: 'Failed to complete all operations. Please contact support.'
    };
  }
}

/**
 * Helper: Convert blob to base64
 */
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      // Remove data URL prefix (e.g., "data:application/pdf;base64,")
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Submit tunnel assessment - uses dedicated tunnel endpoint
 * @param {Object} userDetails - User information
 * @param {Object} assessmentResponses - Assessment form responses
 * @param {string} reportText - Generated report text
 * @returns {Promise<Object>} Response with assessmentId and cloudinaryUrl
 */
export async function submitTunnelAssessment(
  userDetails,
  assessmentResponses,
  reportText
) {
  try {
    // Validate required fields
    if (!userDetails.email || !userDetails.name) {
      throw new Error('User email and name are required');
    }

    console.log('🚇 Submitting tunnel assessment to dedicated endpoint...');
    console.log('🔵 Server will generate comprehensive AI report and create PDF');

    const submitter = await ensureSubmitterDetails();
    if (submitter.cancelled) return cancelledResult();
    const payload = {
      userDetails: userDetails,
      assessmentResponses: assessmentResponses,
      submitterDetails: submitter.details
    };

    console.log('📤 Sending to /api/submit-tunnel-assessment:', { userDetails: userDetails.name, responseKeys: Object.keys(assessmentResponses) });

    const response = await fetch(`${API_BASE_URL}/api/submit-tunnel-assessment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAuthToken()}` },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('❌ submit-tunnel-assessment failed:', err);
      throw new Error(err.error || err.details || 'Failed to submit tunnel assessment');
    }

    const data = await response.json();
    console.log('✅ submit-tunnel-assessment result:', data);
    return { success: true, assessmentId: data.assessmentId, cloudinaryUrl: data.cloudinaryUrl };
  } catch (error) {
    console.error('❌ Error in tunnel assessment submission:', error);
    
    return {
      success: false,
      error: error.message,
      message: 'Failed to submit tunnel assessment. Please contact support.'
    };
  }
}

/**
 * Submit bridge assessment to dedicated endpoint
 * @param {Object} userDetails - User information
 * @param {Object} assessmentResponses - Assessment form responses
 * @param {string} reportText - Generated report text (optional)
 * @returns {Promise<Object>} Response with assessmentId and cloudinaryUrl
 */
export async function submitBridgeAssessment(
  userDetails,
  assessmentResponses,
  reportText
) {
  try {
    if (!userDetails.email || !userDetails.name) {
      throw new Error('User email and name are required');
    }

    console.log('🌉 Submitting bridge assessment to dedicated endpoint...');
    console.log('🔵 Server will generate comprehensive bridge AI report and create PDF');

    const submitter = await ensureSubmitterDetails();
    if (submitter.cancelled) return cancelledResult();
    const payload = {
      userDetails: userDetails,
      assessmentResponses: assessmentResponses,
      reportText: reportText || '',
      submitterDetails: submitter.details
    };

    const response = await fetch(`${API_BASE_URL}/api/submit-bridge-assessment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAuthToken()}` },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('❌ submit-bridge-assessment failed:', err);
      const failure = new Error(err.error || err.message || err.details || 'Failed to submit bridge assessment');
      failure.status = response.status;
      throw failure;
    }

    const data = await response.json();
    console.log('✅ submit-bridge-assessment result:', data);
    return { success: true, assessmentId: data.assessmentId, cloudinaryUrl: data.cloudinaryUrl };
  } catch (error) {
    console.error('❌ Error in bridge assessment submission:', error);
    
    // Tell the user what actually went wrong; their answers are still on this device in every case.
    let message = 'Failed to submit bridge assessment. Please try again, or contact support if it keeps happening.';
    if (error.status === 401) {
      message = 'Your session has expired. Please log in again - your answers are saved on this device.';
    } else if (error.status === 402) {
      message = error.message;
    } else if (error.status === 413) {
      message = 'The assessment is too large to submit. Please remove some very long text entries and try again.';
    } else if (error.status === 400) {
      message = `The assessment could not be submitted: ${error.message}`;
    } else if (error instanceof TypeError) {
      message = 'Could not reach the server. Please check your internet connection and try again - your answers are kept.';
    }

    return {
      success: false,
      error: error.message,
      message
    };
  }
}

/**
 * Get assessment by ID
 */
export async function getAssessment(assessmentId) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/assessment/${assessmentId}`);
    
    if (!response.ok) {
      throw new Error('Assessment not found');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching assessment:', error);
    throw error;
  }
}

/**
 * Get user's assessment history
 */
export async function getUserAssessments(email) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/assessments/user/${encodeURIComponent(email)}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch assessments');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching user assessments:', error);
    throw error;
  }
}

/**
 * Download assessment PDF
 */
export function getAssessmentPdfUrl(assessmentId) {
  return `${API_BASE_URL}/api/assessment/${assessmentId}/pdf`;
}

/**
 * Get authenticated user's assessments (requires login token)
 * @param {string} token - JWT authentication token
 * @returns {Promise<Object>} Response with user's assessments
 */
export async function getUserAssessmentsAuth(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/user/assessments`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to fetch assessments' }));
      throw new Error(error.message || 'Failed to fetch assessments');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching user assessments:', error);
    throw error;
  }
}

/**
 * Get detailed assessment data by ID (requires authentication)
 * @param {string} assessmentId - Assessment ID
 * @param {string} token - JWT authentication token  
 * @returns {Promise<Object>} Detailed assessment data
 */
export async function getAssessmentDetails(assessmentId, token) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/assessment/${assessmentId}`, {
      method: 'GET',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch assessment details');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching assessment details:', error);
    throw error;
  }
}

/**
 * Save advanced questionnaire responses for an existing assessment
 * @param {string} assessmentId - Original assessment ID
 * @param {Object} advancedResponses - Advanced questionnaire responses
 * @param {string} token - JWT authentication token
 * @returns {Promise<Object>} Response with success status
 */
export async function saveAdvancedResponses(assessmentId, advancedResponses, token) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/assessment/${assessmentId}/advanced`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ advancedResponses })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to save advanced responses' }));
      throw new Error(error.message || 'Failed to save advanced responses');
    }

    return await response.json();
  } catch (error) {
    console.error('Error saving advanced responses:', error);
    throw error;
  }
}

/**
 * Get full report data for a single assessment (basic + advanced + final)
 * @param {string} assessmentId - Assessment ID
 * @param {string} token - JWT authentication token
 * @returns {Promise<{basic, advanced, final}>}
 */
export async function getFullAssessmentReport(assessmentId, token) {
  const response = await fetch(`${API_BASE_URL}/api/assessment/${assessmentId}/report`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to fetch report');
  }
  return response.json();
}

/**
 * Get full user assessment list with basic + advanced + final flags
 * @param {string} token - JWT authentication token
 * @returns {Promise<{assessments: Array}>}
 */
export async function getUserFullAssessments(token) {
  const response = await fetch(`${API_BASE_URL}/api/user/full-assessments`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to fetch assessments');
  }
  return response.json();
}

/**
 * Upload one bridge inspection photograph.
 *
 * Bridge questionnaires collect dozens of photographs while the form is being filled,
 * before any Assessment document exists. This stores the image immediately and returns
 * its URL, which the questionnaire keeps alongside the answer so the submission
 * references a real image rather than just a filename.
 *
 * @param {File} file - the selected image
 * @param {string} token - JWT authentication token
 * @returns {Promise<{success: boolean, url: string, publicId: string}>}
 */
export async function uploadBridgePhoto(file, token) {
  const form = new FormData();
  form.append('image', file);

  const response = await fetch(`${API_BASE_URL}/api/bridge/upload-photo`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: form
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data.success) {
    throw new Error(data.message || 'Photo upload failed');
  }
  return data;
}
